import { useState, useEffect } from 'react'
import { LuksoProfileAvatar } from './LuksoProfileAvatar'
import type { Address, Hex } from 'viem'
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts'

interface ControllerInfoProps {
  controllerAddress: Address | null
  onAddressChange: (address: Address) => void
  controllerLabel: string
  onControllerLabelChange: (label: string) => void
}

export function ControllerInfo({
  controllerAddress,
  onAddressChange,
  controllerLabel,
  onControllerLabelChange,
}: ControllerInfoProps) {
  const [inputValue, setInputValue] = useState(controllerAddress || '')
  const [isValidAddress, setIsValidAddress] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showGeneratedKeyModal, setShowGeneratedKeyModal] = useState(false)
  const [generatedPrivateKey, setGeneratedPrivateKey] = useState<Hex | null>(null)
  const [privateKeyCopied, setPrivateKeyCopied] = useState(false)
  const [savedAck, setSavedAck] = useState(false)
  const [showPrivateKey, setShowPrivateKey] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)

  useEffect(() => {
    if (controllerAddress) {
      setInputValue(controllerAddress)
    }
  }, [controllerAddress])

  useEffect(() => {
    // Validate address format
    const isValid = /^0x[a-fA-F0-9]{40}$/.test(inputValue)
    setIsValidAddress(isValid)
  }, [inputValue])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    if (/^0x[a-fA-F0-9]{40}$/.test(value)) {
      onAddressChange(value as Address)
    }
  }

  const handleGenerateControllerKey = () => {
    // Generate entirely in-browser. Nothing is sent to a server.
    const privateKey = generatePrivateKey()
    const account = privateKeyToAccount(privateKey)
    setGeneratedPrivateKey(privateKey)
    setInputValue(account.address)
    onAddressChange(account.address as Address)
    setShowGeneratedKeyModal(true)
    setPrivateKeyCopied(false)
    setSavedAck(false)
    setShowPrivateKey(false)
  }

  const handleCopy = async () => {
    if (controllerAddress) {
      await navigator.clipboard.writeText(controllerAddress)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleCopyPrivateKey = async () => {
    if (!generatedPrivateKey) return
    await navigator.clipboard.writeText(generatedPrivateKey)
    setPrivateKeyCopied(true)
    setTimeout(() => setPrivateKeyCopied(false), 2000)
  }

  const handleDownloadPrivateKey = () => {
    if (!generatedPrivateKey) return
    const lines = [
      'LUKSO Controller Key Backup',
      '',
      `Private Key: ${generatedPrivateKey}`,
      `Controller Address: ${privateKeyToAccount(generatedPrivateKey).address}`,
      '',
      'Important:',
      '- Anyone with this key can control this controller.',
      '- Store this file offline in a secure password manager or encrypted vault.',
      '- There is no recovery if this key is lost.',
    ]
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'controller-key-backup.txt'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="card-hub">
      <div className="space-y-4">
        <div>
          <label className="label">Label (optional)</label>
          <input
            type="text"
            value={controllerLabel}
            onChange={(e) => onControllerLabelChange(e.target.value)}
            maxLength={64}
            placeholder="e.g. Forever Moments App"
            className="input input-subtle"
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5">
              <label className="label mb-0">Address</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setInfoOpen((v) => !v)}
                  className="p-1 rounded text-gray-400 hover:text-gray-300 hover:bg-white/5 transition-colors"
                  title="What is this?"
                  aria-expanded={infoOpen}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                {infoOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      aria-hidden="true"
                      onClick={() => setInfoOpen(false)}
                    />
                    <div
                      className="absolute left-0 top-full mt-1.5 w-72 p-4 rounded-lg border border-white/10 bg-surface shadow-xl z-20 text-left"
                      role="dialog"
                      aria-label="What is this?"
                    >
                      <p className="font-medium text-gray-200">What is this?</p>
                      <p className="mt-1 text-sm text-gray-400">
                        This is the address that will be authorized as a controller on your Universal Profile.
                        By authorizing this controller, you're granting it specific permissions to act on your behalf.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleGenerateControllerKey}
              className="btn-secondary text-xs px-3 py-1.5"
            >
              Generate address
            </button>
          </div>
          <div className="relative">
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="0x..."
              className={`input input-subtle font-mono pr-20 ${
                inputValue && !isValidAddress 
                  ? 'border-red-500 focus:ring-red-500' 
                  : ''
              }`}
            />
            {controllerAddress && (
              <button
                onClick={handleCopy}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                title="Copy address"
              >
                {copied ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                )}
              </button>
            )}
          </div>
          {inputValue && !isValidAddress && (
            <p className="mt-1 text-sm text-red-500">
              Please enter a valid Ethereum address (0x followed by 40 hex characters)
            </p>
          )}
          {controllerAddress && isValidAddress && (
            <div className="mt-3 p-3 rounded-lg border border-white/[0.08] bg-white/[0.04] flex items-center gap-3">
              <LuksoProfileAvatar
                address={controllerAddress}
                size="md"
                showIdenticon={true}
              />
              <div className="flex-1">
                <p className="text-sm font-medium">Controller</p>
                <p className="text-xs text-gray-500 font-mono">
                  {controllerAddress.slice(0, 10)}...{controllerAddress.slice(-8)}
                </p>
              </div>
            </div>
          )}
        </div>

      </div>

      {showGeneratedKeyModal && generatedPrivateKey && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-surface shadow-2xl">
              <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Save your new controller private key</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  This key is shown only now. If you lose it, this controller cannot be recovered.
                </p>
              </div>

              <div className="px-6 py-5 space-y-4">
                <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-900/15 p-4 text-sm text-amber-800 dark:text-amber-200">
                  Never share this private key. Anyone with this key can access this controllers permissions.
                </div>

                <div className="rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/70 dark:bg-neutral-800 p-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Private key</div>
                  <div className="font-mono text-sm break-all text-gray-900 dark:text-gray-100">
                    {showPrivateKey ? generatedPrivateKey : '•'.repeat(66)}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPrivateKey((v) => !v)}
                    className="mt-2 text-xs text-gray-500 dark:text-gray-400 underline hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    {showPrivateKey ? 'Hide key' : 'Show key'}
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary text-sm" onClick={handleCopyPrivateKey}>
                    {privateKeyCopied ? 'Copied' : 'Copy private key'}
                  </button>
                  <button type="button" className="btn-secondary text-sm" onClick={handleDownloadPrivateKey}>
                    Download backup file
                  </button>
                </div>

                <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={savedAck}
                    onChange={(e) => setSavedAck(e.target.checked)}
                    className="mt-1 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-800 text-lukso-pink focus:ring-lukso-pink"
                  />
                  <span>I saved this private key in a secure place.</span>
                </label>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowGeneratedKeyModal(false)
                    setGeneratedPrivateKey(null)
                  }}
                >
                  Close anyway
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!savedAck}
                  onClick={() => {
                    setShowGeneratedKeyModal(false)
                    setGeneratedPrivateKey(null)
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
